import { yupResolver } from '@hookform/resolvers/yup/dist/yup.js'
import { Alert, Box, Button, Popover, Tab, Tabs, TextField, Typography } from '@mui/material'
import { getAxiosErrorMessage } from 'api/axios/helpers'
import type { AxiosError } from 'axios'
import { CoreMyntModal } from 'components/modals/CoreMyntModal'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import {
  useCategoryRule,
  useGetCategoryTranslations,
  useUpdateCategory,
  useUpdateCategoryRule,
  useUpdateCategoryTranslations,
} from './adapters'
import { isValidOrderInput } from './formUtils'
import { RulesTab } from './RulesTab'
import type { SpendControlRow } from './SpendControlTable'
import { TranslationsTab } from './TranslationsTab'
import { useRulesFields } from './useRulesFields'
import { useTranslationsFields } from './useTranslationsFields'

type MainTab = 'details' | 'translations' | 'rules'

type FormValues = {
  name: string
  description: string
  order: string
}

const schema = yup.object({
  name: yup.string().trim().required(),
  description: yup.string().trim().required(),
  order: yup
    .string()
    .default('')
    .test('valid-order', 'Must be a non-negative integer', (v) => isValidOrderInput(v ?? '')),
})

export const EditCategoryModal = ({
  open,
  onClose,
  row,
  partner,
}: {
  open: boolean
  onClose: () => void
  row: SpendControlRow
  partner: string | undefined
}) => {
  const [mainTab, setMainTab] = useState<MainTab>('details')

  const {
    register,
    handleSubmit,
    reset: resetForm,
    formState: { isValid, isDirty, errors },
  } = useForm<FormValues>({
    defaultValues: {
      name: row.name,
      description: row.description ?? '',
      order: row.order != null ? String(row.order) : '',
    },
    mode: 'onChange',
    resolver: yupResolver(schema),
  })

  const { mutate: mutateDetails, isLoading: detailsLoading, error: detailsError, reset: detailsReset } = useUpdateCategory()
  const { mutate: mutateTranslations, isLoading: transLoading, error: transError } = useUpdateCategoryTranslations()
  const { mutate: mutateRule, isLoading: ruleLoading, error: ruleError } = useUpdateCategoryRule()
  const { mccs, mids: loadedMids, expression } = useCategoryRule({ categoryId: row.id, partner })
  const { data: loadedTranslations } = useGetCategoryTranslations({ categoryId: row.id })

  const {
    translationFields,
    updateTranslation,
    markBlurred,
    buildPatchPayload,
    translationsValidationErrors,
    hasTranslationsValidationErrors,
    isTranslationsChanged,
  } = useTranslationsFields(loadedTranslations)
  const { isRulesValid, isRulesChanged, mcc, mids } = useRulesFields({ mccs, mids: loadedMids, partner })

  const handleClose = () => {
    resetForm()
    detailsReset()
    onClose()
  }

  const onDetailsSubmit = handleSubmit((data) => {
    const parsedOrder = data.order.trim() === '' ? null : Number(data.order.trim())
    mutateDetails(
      { id: row.id, name: data.name.trim(), description: data.description.trim(), order: parsedOrder },
      { onSuccess: handleClose },
    )
  })

  const handleTranslationsSave = () => {
    const patchPayload = buildPatchPayload()
    if (patchPayload.length > 0) {
      mutateTranslations({ id: row.id, translations: patchPayload }, { onSuccess: handleClose })
    }
  }

  const handleRulesSave = () => {
    if (!isRulesValid) return
    mutateRule({ id: row.id, mccs: mcc.parsed, mids: mids.parsed }, { onSuccess: handleClose })
  }

  return (
    <CoreMyntModal
      wrapperProps={{ style: { minWidth: 800 } }}
      title="Edit category"
      onClose={handleClose}
      open={open}
      actions={() => (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 1,
            }}
          >
            {mainTab === 'details' && (
              <Button variant="contained" disabled={!isValid || !isDirty || detailsLoading} onClick={onDetailsSubmit}>
                {detailsLoading ? 'Saving...' : 'Save'}
              </Button>
            )}
            {mainTab === 'translations' && (
              <Button
                variant="contained"
                disabled={!isTranslationsChanged || hasTranslationsValidationErrors || transLoading}
                onClick={handleTranslationsSave}
              >
                {transLoading ? 'Saving...' : 'Save translations'}
              </Button>
            )}
            {mainTab === 'rules' && (
              <Button
                variant="contained"
                disabled={!isRulesValid || !isRulesChanged || mcc.editMode || mids.editMode || ruleLoading}
                onClick={handleRulesSave}
              >
                {ruleLoading ? 'Saving...' : 'Save rules'}
              </Button>
            )}
          </Box>
        </Box>
      )}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {detailsError instanceof Error && mainTab === 'details' && (
          <Alert severity="error">
            {(detailsError as AxiosError).response ? getAxiosErrorMessage(detailsError as AxiosError) : detailsError.message}
          </Alert>
        )}
        {transError instanceof Error && mainTab === 'translations' && (
          <Alert severity="error">
            {(transError as AxiosError).response ? getAxiosErrorMessage(transError as AxiosError) : transError.message}
          </Alert>
        )}
        {ruleError instanceof Error && mainTab === 'rules' && (
          <Alert severity="error">
            {(ruleError as AxiosError).response ? getAxiosErrorMessage(ruleError as AxiosError) : ruleError.message}
          </Alert>
        )}

        <Tabs value={mainTab} onChange={(_, v) => setMainTab(v)}>
          <Tab label="Details" value="details" />
          <Tab label="Translations" value="translations" />
          <Tab label="Rules" value="rules" />
        </Tabs>

        {mainTab === 'details' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <TextField label="Name" {...register('name')} required />
            <TextField label="Description" {...register('description')} required />
            <TextField label="Order" type="number" {...register('order')} error={!!errors.order} />
          </Box>
        )}

        {mainTab === 'translations' && (
          <TranslationsTab
            currentName={row.name}
            currentDescription={row.description}
            translationFields={translationFields}
            translationsValidationErrors={translationsValidationErrors}
            onUpdate={updateTranslation}
            onBlur={markBlurred}
          />
        )}

        {mainTab === 'rules' && <RulesTab expression={expression} mcc={mcc} mids={mids} />}
      </Box>
    </CoreMyntModal>
  )
}
